import { memo, useRef } from 'react';
import type { Measure } from '../../music/piece';
import type { PieceView } from '../model';
import { BASS_CLEF, BASS_DOTS, TREBLE_BALL, TREBLE_CLEF, accidental, brace, cClef } from '../engrave/glyphs';
import { CHUNK, Chunk, Engraving, keySignatureSteps } from '../engrave/layout';

/**
 * The score as the edition writes it, on one long system that moves under a fixed playhead:
 * beams, rests, ties, stems by voice, accidentals by the bar, clefs and signatures. Clef and
 * key signature stay pinned at the left; the tension of each chord runs underneath on the same
 * time axis. Drag to move through the music, tap to jump.
 */

interface Props {
  eng: Engraving;
  measures: Measure[];
  view: PieceView;
  position: number;
  width: number;
  /** Pixels per staff space. */
  space: number;
  fog: boolean;
  band: number;
  label: string;
  onSeek: (t: number) => void;
}

const BOTTOM = { G: 30, G8: 23, F: 18, C3: 24, C4: 22 } as const;

function interp(cols: Array<{ t: number; x: number }>, t: number): number {
  if (!cols.length) return 0;
  if (t <= cols[0].t) return cols[0].x;
  let lo = 0;
  let hi = cols.length - 1;
  if (t >= cols[hi].t) return cols[hi].x;
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1;
    if (cols[m].t <= t) lo = m;
    else hi = m;
  }
  const a = cols[lo];
  const b = cols[hi];
  return a.x + ((t - a.t) / (b.t - a.t || 1)) * (b.x - a.x);
}

function inverse(cols: Array<{ t: number; x: number }>, x: number): number {
  if (!cols.length) return 0;
  if (x <= cols[0].x) return cols[0].t;
  let lo = 0;
  let hi = cols.length - 1;
  if (x >= cols[hi].x) return cols[hi].t;
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1;
    if (cols[m].x <= x) lo = m;
    else hi = m;
  }
  const a = cols[lo];
  const b = cols[hi];
  return a.t + ((x - a.x) / (b.x - a.x || 1)) * (b.t - a.t);
}

const ChunkView = memo(function ChunkView({ c }: { c: Chunk }) {
  return (
    <g>
      {c.hair.length > 0 && <path className="eng-hair" d={c.hair.join('')} />}
      {c.fill.length > 0 && <path className="eng-fill" d={c.fill.join('')} />}
      {c.eo.length > 0 && <path className="eng-fill" fillRule="evenodd" d={c.eo.join('')} />}
      {c.raw.length > 0 && <g dangerouslySetInnerHTML={{ __html: c.raw.join('') }} />}
      {c.text.map((t, i) => (
        <text key={i} x={t.x} y={t.y} className={t.cls} textAnchor={t.cls === 'eng-num' ? 'start' : 'middle'}>
          {t.s}
        </text>
      ))}
    </g>
  );
});

function EngravedStripInner({ eng, measures, view, position, width, space: sp, fog, band, label, onSeek }: Props) {
  const H = eng.height * sp + band;
  const now = interp(eng.cols, position);

  // The pinned panel at the left: brace, clefs, key signature of the music just under it.
  const fifthsProbe = eng.keyAt(position);
  const panelW = Math.round((2.2 + 3.0 + Math.abs(fifthsProbe) * 1.0 + 0.9) * sp);
  const playX = panelW + Math.max(48, (width - panelW) * 0.3);
  const scroll = now - (playX - panelW) / sp; // x (sp) at the panel's edge
  const tLeft = inverse(eng.cols, scroll);
  const fifths = eng.keyAt(tLeft);

  const first = Math.floor((scroll - CHUNK) / CHUNK);
  const lastChunk = Math.floor((now + (width - playX) / sp) / CHUNK);
  const visible: number[] = [];
  for (let i = Math.max(0, first); i <= lastChunk; i++) if (eng.chunks.has(i)) visible.push(i);

  // Notes sounding now, drawn over in the highlight colour.
  const on: string[] = [];
  const onEo: string[] = [];
  {
    let lo = 0;
    let hi = eng.heads.length;
    while (lo < hi) {
      const m = (lo + hi) >> 1;
      if (eng.heads[m].t <= position + 1e-6) lo = m + 1;
      else hi = m;
    }
    for (let i = lo - 1; i >= 0 && i >= lo - 400; i--) {
      const h = eng.heads[i];
      if (h.t <= position + 1e-6 && position < h.end - 1e-6) (h.eo ? onEo : on).push(h.d);
    }
  }

  const tx = playX - now * sp;
  const px = (t: number) => tx + interp(eng.cols, t) * sp;

  // Tension band under the staves.
  const t0 = inverse(eng.cols, scroll - 2);
  const t1 = inverse(eng.cols, now + (width - playX) / sp + 2);
  const bandEvents = band > 0 ? view.events.filter((ev) => ev.end >= t0 && ev.start <= (fog ? Math.min(t1, position) : t1)) : [];

  const drag = useRef<{ x: number; at: number; moved: boolean; id: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const localX = (e: React.PointerEvent) => e.clientX - (svgRef.current?.getBoundingClientRect().left ?? 0);
  const total = measures.length ? measures[measures.length - 1].start + measures[measures.length - 1].length : eng.end;
  const clamp = (t: number) => Math.max(0, Math.min(total - 1e-3, t));

  return (
    <svg
      ref={svgRef}
      className="strip-svg eng"
      width={width}
      height={H}
      viewBox={`0 0 ${width} ${H}`}
      role="img"
      aria-label={label}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        drag.current = { x: localX(e), at: now, moved: false, id: e.pointerId };
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d || d.id !== e.pointerId) return;
        const dx = localX(e) - d.x;
        if (!d.moved && Math.abs(dx) < 6) return;
        if (!d.moved) {
          d.moved = true;
          (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
        }
        onSeek(clamp(inverse(eng.cols, d.at - dx / sp)));
      }}
      onPointerUp={(e) => {
        const d = drag.current;
        drag.current = null;
        if (!d || d.moved) return;
        const lx = localX(e);
        if (lx < panelW) return;
        onSeek(clamp(inverse(eng.cols, now + (lx - playX) / sp)));
      }}
      onPointerCancel={() => (drag.current = null)}
    >
      <defs>
        <clipPath id="eng-music">
          <rect x={panelW} y={0} width={Math.max(0, width - panelW)} height={H} />
        </clipPath>
      </defs>
      <g clipPath="url(#eng-music)">
        {bandEvents.map((ev) => {
          const v = view.tension.perEvent[ev.index]?.value ?? 0;
          const a = Math.max(px(ev.start), panelW);
          const b = Math.min(px(fog ? Math.min(ev.end, position) : ev.end), width);
          if (b <= a) return null;
          return (
            <rect
              key={ev.index}
              x={a}
              y={eng.height * sp + 3}
              width={Math.max(0, b - a - 1)}
              height={Math.max(2, band - 6)}
              rx={2}
              style={{ fill: `color-mix(in oklab, var(--tension) ${Math.round(Math.min(1, v) * 100)}%, var(--rest))`, opacity: 0.35 + 0.55 * Math.min(1, v) }}
            />
          );
        })}
        <g transform={`translate(${tx.toFixed(2)},0) scale(${sp})`}>
          {visible.map((i) => (
            <ChunkView key={i} c={eng.chunks.get(i)!} />
          ))}
          {on.length > 0 && <path className="eng-on" d={on.join('')} />}
          {onEo.length > 0 && <path className="eng-on" fillRule="evenodd" d={onEo.join('')} />}
        </g>
      </g>

      {/* Pinned: brace, clefs, key signature */}
      <rect x={0} y={0} width={panelW} height={eng.height * sp} className="strip-panel" />
      <g transform={`scale(${sp})`}>
        {eng.staffTop.map((top, si) => (
          <g key={si}>
            {[0, 1, 2, 3, 4].map((l) => (
              <line key={l} x1={1.4} x2={panelW / sp} y1={top + l} y2={top + l} className="eng-hair-l" />
            ))}
            <PinnedClef clef={eng.clefAt(si + 1, tLeft)} top={top} />
            <PinnedKey fifths={fifths} clef={eng.clefAt(si + 1, tLeft)} top={top} />
          </g>
        ))}
        <line x1={1.4} x2={1.4} y1={eng.staffTop[0]} y2={eng.staffTop[eng.staves - 1] + 4} className="eng-hair-l" />
        {eng.staves > 1 && <path className="eng-fill" d={brace(1.1, eng.staffTop[0], eng.staffTop[eng.staves - 1] + 4)} />}
      </g>
      <line x1={panelW} x2={panelW} y1={eng.staffTop[0] * sp} y2={(eng.staffTop[eng.staves - 1] + 4) * sp} className="strip-edge" />

      <line x1={playX} x2={playX} y1={2} y2={H - 2} className="strip-playhead" />
      <path d={`M${playX - 5},1 h10 l-5,6 z`} className="strip-playhead-cap" />
    </svg>
  );
}

function PinnedClef({ clef, top }: { clef: keyof typeof BOTTOM; top: number }) {
  const x = 1.9;
  if (clef === 'G' || clef === 'G8')
    return (
      <g className="eng-pen" transform={`translate(${x},${top})`}>
        <path d={TREBLE_CLEF} strokeWidth={0.22} />
        <circle cx={TREBLE_BALL[0]} cy={TREBLE_BALL[1]} r={TREBLE_BALL[2]} />
        {clef === 'G8' && (
          <text x={1.2} y={7.6} className="eng-small8">
            8
          </text>
        )}
      </g>
    );
  if (clef === 'F')
    return (
      <g className="eng-pen" transform={`translate(${x},${top})`}>
        <path d={BASS_CLEF} strokeWidth={0.26} />
        {BASS_DOTS.map(([a, b, r], i) => (
          <circle key={i} cx={a} cy={b} r={r} />
        ))}
      </g>
    );
  return <path className="eng-fill" d={cClef(x, top + (clef === 'C3' ? 2 : 1))} />;
}

function PinnedKey({ fifths, clef, top }: { fifths: number; clef: keyof typeof BOTTOM; top: number }) {
  const items = keySignatureSteps(fifths, clef);
  let fill = '';
  let eo = '';
  items.forEach(([step, alter], i) => {
    const y = top + 4 - (step - BOTTOM[clef]) / 2;
    const g = accidental(alter, 5.6 + i * 1.0, y);
    fill += g.fill;
    eo += g.eo;
  });
  return (
    <>
      {fill && <path className="eng-fill" d={fill} />}
      {eo && <path className="eng-fill" fillRule="evenodd" d={eo} />}
    </>
  );
}

export const EngravedStrip = memo(EngravedStripInner);
